import { pure } from '@core/helpers/pure';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  DeviceDetectorService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestCommentsService } from '@core/services/request/request-comments.service';
import { RequestService } from '@core/services/request/request.service';
import {
  TuiScrollbar,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiCheckbox,
  TuiTextfield,
  TuiLabel,
} from '@taiga-ui/core';
import {
  TuiBadge,
  TuiAvatar,
  TuiAutoColorPipe,
  TuiTextarea,
  TuiInitialsPipe,
  TuiButtonLoading,
  TuiSkeleton,
} from '@taiga-ui/kit';
import { environment } from '@environment/environment';
import {
  RequestFormCommentsTranslations,
  TextCopierTranslations,
} from '@core/types';
import type {
  RequestComment,
  RequestFormCommentsTranslationsPayload,
  TextCopierTranslationsPayload,
  User,
} from '@core/types';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { asapScheduler, BehaviorSubject, EMPTY } from 'rxjs';
import { isUserPopulated } from '@core/helpers';
import type { Nullable } from '@core/helpers';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { EmptyBlockComponent } from '@core/components/empty-block';
import { SafeHtmlPipe } from '@core/pipes';
import { ClipboardService } from 'ngx-clipboard';

@Component({
  selector: 'x-request-form-comments',
  imports: [
    TuiSkeleton,
    TuiButtonLoading,
    TuiTextfield,
    TuiInitialsPipe,
    TuiIcon,
    TuiAvatar,
    DatePipe,
    AsyncPipe,
    NgClass,
    TuiTextarea,
    ReactiveFormsModule,
    TuiButton,
    TuiHint,
    TranslatePipe,
    TuiAutoColorPipe,
    TuiLabel,
    TuiBadge,
    EmptyBlockComponent,
    SafeHtmlPipe,
    TuiCheckbox,
    FormsModule,
    TuiScrollbar,
  ],
  templateUrl: './request-form-comments.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormCommentsComponent implements OnInit {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  $requestComments = input<Nullable<RequestComment[]>>(null, {
    alias: 'requestComments',
  });

  private readonly requestCommentsService = inject(RequestCommentsService);
  private readonly accountService = inject(AccountService);
  private readonly requestService = inject(RequestService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  private translates: RequestFormCommentsTranslationsPayload &
    TextCopierTranslationsPayload = {
    [RequestFormCommentsTranslations.DialogDeleteComment]:
      'Вы уверены, что хотите удалить комментарий?',
    [RequestFormCommentsTranslations.DialogDeleteSelectedComments]:
      'Вы уверены, что хотите удалить выбранные комментарии?',
    [RequestFormCommentsTranslations.DialogDeleteSelectedCommentsTitle]:
      'Удаление выбранных комментариев',
    [RequestFormCommentsTranslations.DialogDeleteCommentTitle]:
      'Удаление комментария',
    [RequestFormCommentsTranslations.DialogDeleteCommentYes]: 'Да',
    [RequestFormCommentsTranslations.DialogDeleteCommentNo]: 'Нет',
    [TextCopierTranslations.Copied]: 'Скопировано',
    [TextCopierTranslations.NotFoundToCopy]: 'Нечего копировать',
  };
  private readonly destroyRef = inject(DestroyRef);
  private readonly xAlertService = inject(XAlertService);
  public readonly deviceService = inject(DeviceDetectorService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly user$ = this.accountService.currentUser$;
  public isInitialLoad = true;

  protected readonly isUserPopulated = isUserPopulated;

  readonly $request = this.requestService.$request;
  public requestComments: RequestComment[] = [];

  public $selectMode = signal(false);
  public selectedComments = new Set<number>();

  @ViewChild('commentsContainer') commentsContainer!: ElementRef;

  constructor() {
    effect(() => {
      if (this.$requestComments()) {
        this.requestComments = this.$requestComments()!;
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
        this.scrollCommentsToBottom();
      }
    });

    // Clear selection when select mode is turned off
    effect(() => {
      if (!this.$selectMode()) {
        this.selectedComments.clear();
      }
    });
  }

  ngOnInit(): void {
    if (!this.$isInsideSummary()) {
      this.requestCommentsService.entities$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(comments => {
          if (this.isInitialLoad) {
            this.isInitialLoad = false;
          }
          this.requestComments = comments;
        });
    }

    this.outkeepTranslateService
      .translate([
        ...Object.values(RequestFormCommentsTranslations),
        ...Object.values(TextCopierTranslations),
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  newCommentText = new FormControl('', Validators.required);

  $loading = signal<boolean>(false);

  getAvatarUrl(createdId: User | number): string {
    if (
      isUserPopulated(createdId) &&
      environment.staticUrl &&
      createdId.avatar
    ) {
      return environment.staticUrl + createdId.avatar;
    }

    return '';
  }

  addComment() {
    if (this.newCommentText.invalid || !this.newCommentText.value) {
      return;
    }
    this.$loading.set(true);
    this.requestCommentsService
      .createRequestComment$(this.$request().id, this.newCommentText.value)
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.$loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.newCommentText.reset();
        this.requestCommentsService.refreshEntities();
      });
  }

  deleteComment(commentId: number) {
    const msg =
      this.translates[RequestFormCommentsTranslations.DialogDeleteComment];

    this.$loading.set(true);
    this.xAlertService
      .showDialog(
        this.translates[
          RequestFormCommentsTranslations.DialogDeleteCommentTitle
        ],
        msg,
        this.translates[RequestFormCommentsTranslations.DialogDeleteCommentYes],
        this.translates[RequestFormCommentsTranslations.DialogDeleteCommentNo],
      )
      .pipe(
        switchMap(result => {
          if (result) {
            return this.requestCommentsService.deleteRequestComment$(commentId);
          }

          return EMPTY;
        }),
        catchError(() => EMPTY),
        finalize(() => this.$loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.requestCommentsService.refreshEntities();
      });
  }

  public toggleCommentSelection(commentId: number, checked: boolean): void {
    if (!this.$selectMode()) return;

    if (checked) {
      this.selectedComments.add(commentId);
    } else {
      this.selectedComments.delete(commentId);
    }
  }

  public isCommentSelected(commentId: number): boolean {
    return this.selectedComments.has(commentId);
  }

  public getSelectedCommentsText(): string {
    return this.requestComments
      .filter(c => this.selectedComments.has(c.id))
      .map(c => c.text)
      .join('\n\n');
  }

  public copySelectedComments(): void {
    if (this.selectedComments.size === 0) return;

    const textToCopy = this.getSelectedCommentsText();

    if (!textToCopy) {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.NotFoundToCopy],
        {
          appearance: 'warning',
          autoClose: 2000,
        },
      );

      return;
    }
    this.clipboardService.copy(textToCopy);
    this.xAlertService.showMessage(
      this.translates[TextCopierTranslations.Copied],
      {
        appearance: 'positive',
        autoClose: 2000,
      },
    );
  }

  deleteSelectedComments() {
    const ids = Array.from(this.selectedComments);

    if (!ids.length) return;
    this.$loading.set(true);

    const msg =
      this.translates[
        RequestFormCommentsTranslations.DialogDeleteSelectedComments
      ];

    this.xAlertService
      .showDialog(
        this.translates[
          RequestFormCommentsTranslations.DialogDeleteSelectedCommentsTitle
        ],
        msg,
        this.translates[RequestFormCommentsTranslations.DialogDeleteCommentYes],
        this.translates[RequestFormCommentsTranslations.DialogDeleteCommentNo],
      )
      .pipe(
        switchMap(result => {
          if (result) {
            return this.requestCommentsService.deleteMultipleRequestComments$(
              ids,
            );
          }

          return EMPTY;
        }),
        catchError(() => EMPTY),
        finalize(() => this.$loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.selectedComments.clear();
        this.$selectMode.set(false);
        this.requestCommentsService.refreshEntities();
      });
  }

  private scrollCommentsToBottom(): void {
    asapScheduler.schedule(() => {
      const container = this.commentsContainer?.nativeElement;

      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 500);
  }

  public toggleCommentsSelectMode(): void {
    this.$selectMode.update(value => !value);
  }

  @pure
  public prevCommentWithSameAuthor(
    comment: RequestComment,
    index: number,
  ): boolean {
    if (index === 0) {
      return false;
    }

    const prevComment = this.requestComments[index - 1];

    return (
      isUserPopulated(comment.createdId) &&
      isUserPopulated(prevComment.createdId) &&
      comment.createdId.id === prevComment.createdId.id
    );
  }

  @pure
  public nextCommentWithSameAuthor(
    comment: RequestComment,
    index: number,
  ): boolean {
    if (index === this.requestComments.length - 1) {
      return false;
    }

    const nextComment = this.requestComments[index + 1];

    return (
      isUserPopulated(comment.createdId) &&
      isUserPopulated(nextComment.createdId) &&
      comment.createdId.id === nextComment.createdId.id
    );
  }
}
